import {
  formatDisplayDate,
  getRangeForPreset,
  toDateInputValue,
  WalletDatePreset,
  WALLET_DATE_PRESET_OPTIONS,
} from '../wallet/walletDateRange'
import {formatDateToYearMonth} from '../../common-function/formatDateToYearMonth'

export {getRangeForPreset, toDateInputValue}

export type TripDatePreset = WalletDatePreset | 'month'

export type TripFilterState = {
  preset: TripDatePreset
  date_from: string
  date_to: string
  label: string
  all_time?: boolean
  /** YYYY-MM when preset is `month` */
  month?: string
  status: string[]
}

export const TRIP_DATE_PRESET_OPTIONS: {id: TripDatePreset; label: string}[] = [
  ...WALLET_DATE_PRESET_OPTIONS,
  {id: 'month', label: 'By month (calendar)'},
]

export const TRIP_STATUS_OPTIONS: {value: string; label: string}[] = [
  {value: 'not_started', label: 'Not started'},
  {value: 'in_progress', label: 'In progress'},
  {value: 'completed', label: 'Completed'},
  {value: 'cancel', label: 'Cancel'},
  {value: 'unsuccessful', label: 'Unsuccessful'},
  {value: 'driver_move_on', label: 'Driver move on'},
  {value: 'driver_not_show', label: 'Driver not show'},
  {value: 'deactivate', label: 'Deactivate'},
]

const STATUS_LABELS: Record<string, string> = Object.fromEntries(
  TRIP_STATUS_OPTIONS.map((o) => [o.value, o.label])
)

export const getRangeForMonth = (monthStr: string): Omit<TripFilterState, 'status'> => {
  const ISO_MONTH = /^\d{4}-\d{2}$/
  const raw = ISO_MONTH.test(monthStr.trim())
    ? monthStr.trim()
    : formatDateToYearMonth(new Date()) || new Date().toISOString().slice(0, 7)

  const [y, m] = raw.split('-').map(Number)
  const from = new Date(y, m - 1, 1)
  const to = new Date(y, m, 0)

  return {
    preset: 'month',
    month: raw,
    date_from: toDateInputValue(from),
    date_to: toDateInputValue(to),
    label: `Month ${raw}`,
    all_time: false,
  }
}

export const getDefaultTripFilter = (): TripFilterState => {
  const range = getRangeForPreset('today')
  return {...range, status: []}
}

export const getTripStatusLabel = (value: string): string =>
  STATUS_LABELS[value] || value

export const buildTripFilterSummary = (
  filter: TripFilterState,
  search: string,
  totalRecords?: number
): string => {
  const rangeText = filter.all_time
    ? 'all dates'
    : filter.preset === 'month' && filter.month
      ? filter.month
      : filter.date_from === filter.date_to
        ? formatDisplayDate(filter.date_from)
        : `${formatDisplayDate(filter.date_from)} – ${formatDisplayDate(filter.date_to)}`

  const statusText =
    filter.status.length > 0
      ? `${filter.status.length} status${filter.status.length === 1 ? '' : 'es'}`
      : 'all statuses'

  const parts = [filter.label, `(${rangeText})`, statusText]
  if (search.trim()) parts.push(`search: "${search.trim()}"`)
  if (totalRecords !== undefined) {
    parts.push(`${totalRecords} trip${totalRecords === 1 ? '' : 's'}`)
  }
  return parts.join(' · ')
}
